/**
 * Where each client's user-management *writes* go, and which of them exist.
 *
 * The read path went per-client long ago (`getUserEndpoint`); the writes were
 * left pointing at Ergo's routes, which is why Add User and Edit User 404ed for
 * everyone else. Both now come from `src/config/clients/<key>.json`, the same
 * place the list endpoint comes from.
 *
 * Deliberately free of axios and authService imports — like apiGeneration.js —
 * so it can be unit tested: CRA 5's Jest does not transform node_modules and
 * axios v1's entry point is ESM, so anything importing axios cannot be tested
 * here.
 *
 * Three states per route, and the third is the point:
 *
 *   key absent   → the Ergo default below. Ergo, its property line and any
 *                  unknown client keep behaving exactly as they did.
 *   key a string → that route.
 *   key `null`   → **this backend has no such route.** Not an oversight: it
 *                  means someone probed and found nothing, so the UI declines
 *                  the action instead of firing a request that 404s.
 *
 * Probed unauthenticated with a CORS preflight, where the route's own `Allow`
 * header (not the middleware's blanket Access-Control-Allow-Methods) says which
 * verbs it takes, and 404 means no such URL. Each host was calibrated against
 * near-miss controls — /adduser, /add_users, /create_user, /nonsense_… all 404:
 *
 *   ergobaltic.dev.aiapi        /client_user      Allow: GET, POST, DELETE, HEAD
 *                               /client_user/<b64>/  Allow: PUT
 *   ergobalticproperty.dev.api  both the same — so the default suits property too
 *   bagic.newportalapi          /add_user         Allow: POST      /client_user 404
 *   hegicpp.api                 /add_user         Allow: POST      /client_user 404
 *
 * No host but Ergo's has a *general* edit route under any name tried
 * (/edit_user, /update_user, /modify_user, /user_edit, /update_user_details,
 * /edit_client_user, /update_client_user, /get_user/<b64>/, /add_user/<b64>/).
 * See HEGIC_API.md § User management for what the backend still owes us.
 *
 * Hegic does have `PUT /edit_user/{id}/`, confirmed by a captured request —
 * but only for one narrow thing: `keyValue={"flag": 0 | 1}`, suspend/activate,
 * nothing else in the body. That is not the general edit route the probe
 * above went looking for and came up empty on, so it is its own key
 * (`userStatusEndpoint`) rather than overloading `editUserEndpoint` with a
 * shape this module has no evidence it accepts.
 *
 * Ergo property's suspend/activate is the opposite case: it is the *same*
 * route as its general edit (`PUT /client_user/{id_b64}/`), just a partial
 * `keyValue` carrying only `flag` — confirmed by a captured request. It still
 * gets its own `userStatusEndpoint` entry (pointed at that same template)
 * rather than teaching the UI to build a partial edit body on the fly; see
 * setUserStatus in userService.js for how the partial body actually happens.
 */

import { getClient } from "../config/clients";

/** Ergo's routes, which every client used to get whether they had them or not. */
export const DEFAULT_ADD_USER_ENDPOINT = "/client_user";
export const DEFAULT_EDIT_USER_ENDPOINT = "/client_user/{id_b64}/";

const sessionClientKey = () => {
  try {
    return localStorage.getItem("source_name");
  } catch {
    /* localStorage unavailable */
    return null;
  }
};

/**
 * A declared endpoint, the default when the client says nothing, or null when
 * the client says there is no such route.
 *
 * `in` rather than `||` on purpose: `null` and a missing key mean opposite
 * things here, and `||` collapses them into the same answer — the Ergo default,
 * which is the bug this module exists to fix.
 */
const endpointFrom = (client, field, fallback) => {
  if (!client || !(field in client)) return fallback;
  const declared = client[field];
  return typeof declared === "string" && declared.trim() ? declared.trim() : null;
};

/**
 * The add and edit routes for a client, each a path or null.
 *
 * Resolved at call time from the in-repo registry, not from the portal-config
 * API: a remote record seeded before these keys existed cannot know a route is
 * missing, and the wrong answer here is a 404 the user sees. Same call already
 * made for the API host and for tab visibility.
 */
export const resolveUserWriteEndpoints = (clientKey) => {
  const client = getClient(clientKey || sessionClientKey());
  return {
    add: endpointFrom(client, "addUserEndpoint", DEFAULT_ADD_USER_ENDPOINT),
    edit: endpointFrom(client, "editUserEndpoint", DEFAULT_EDIT_USER_ENDPOINT),
    // No client defaults to this — a one-click suspend/activate is extra,
    // distinct from the full edit form, and only named where a client's file
    // has offered confirmed evidence of it (Hegic, Ergo property so far).
    // Absent key means "no such capability here", same as null.
    status: endpointFrom(client, "userStatusEndpoint", null),
  };
};

/**
 * Put a user id into an edit-route template.
 *
 * `{id_b64}` is base64 — Ergo's route takes the id that way — and `{id}` is the
 * id verbatim, so a backend that does not share Ergo's convention can be
 * configured rather than special-cased.
 *
 * The base64 is substituted unescaped, which is what the hardcoded
 * `/client_user/${btoa(userId)}/` did: escaping it would change the URL Ergo
 * receives for any id whose encoding contains "+" or "/", and this change is
 * meant to leave Ergo byte-identical.
 */
export const fillUserEndpoint = (template, userId) => {
  if (!template) return template;
  const raw = userId == null ? "" : String(userId);
  return template
    .replace(/\{id_b64\}/g, () => btoa(raw))
    .replace(/\{id\}/g, () => raw);
};

const clientLabel = (clientKey) => {
  const key = clientKey || sessionClientKey();
  return getClient(key)?.label || key || "this client";
};

const ACTION_COPY = {
  add: { gerund: "Adding", noun: "add" },
  edit: { gerund: "Editing", noun: "edit" },
  status: { gerund: "Suspending or re-activating", noun: "status-change" },
};

/**
 * Why the portal is refusing, in words a user can act on — no route exists, so
 * this is not something retrying or a different password fixes.
 */
export const describeUnsupportedUserWrite = (action, clientKey) => {
  const copy = ACTION_COPY[action] || ACTION_COPY.edit;
  return (
    `${copy.gerund} users is not available for ${clientLabel(clientKey)} yet: ` +
    `its backend has no ${copy.noun}-user endpoint. ` +
    `The route has to be added on the API side first.`
  );
};

export const USER_WRITE_UNSUPPORTED = "USER_WRITE_UNSUPPORTED";

/**
 * Thrown before any request goes out. Tagged with a `code` rather than a
 * subclass so `instanceof` survives transpilation.
 */
export const userWriteUnsupportedError = (action, clientKey) => {
  const error = new Error(describeUnsupportedUserWrite(action, clientKey));
  error.code = USER_WRITE_UNSUPPORTED;
  error.action = action;
  return error;
};
